import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { validateEmail, validateName } from "../../../utils/validation";
import { useState } from "react";
import { Button, Input, Loading } from "src/components/shared";
import { formatCurrency } from "../../../utils/formatCurrency";
import { useStore } from "../../../store";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Card } from "./card.svg";
import { ReactComponent as Bank } from "./bank.svg";
import { ReactComponent as Cash } from "./cash.svg";
import moment from "moment";
import { toast } from "react-toastify";
import { payForSession } from "src/store/session";

interface Props {
  email: string;
  setEmail: (email: string) => void;
  price: number;
}

const TunisianCheckoutForm = ({ email, setEmail, price }: Props) => {
  const { userStore, courseStore, paymentStore } = useStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const session = courseStore?.classDetails;

  const [selectedMethod, setSelectedMethod] = useState("Konnect");

  const paymentMethods = [
    {
      name: "Konnect",
      description: "Pay with Card",
      icon: <Card />,
    },
    {
      name: "Bank",
      description: "Bank Transfert",
      icon: <Bank />,
    },
    {
      name: "Cash",
      description: "Pay Cash",
      icon: <Cash />,
    },
  ];

  const [fullName, setFullName] = useState(userStore?.user?.fullName || "");
  const [phone, setPhone] = useState(userStore?.user?.phone || "");
  const [loading, setLoading] = useState(false);

  const pay = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!userStore?.user) {
      const tempUser = await userStore.createTempUser({ email, fullName });
      localStorage.setItem("registrationStep", "2");
      localStorage.setItem(
        "registrationUser",
        JSON.stringify({ email, fullName })
      );
    }

    if (selectedMethod === "Konnect") {
      await handleKonnectPayment();
    } else {
      await handleOnSiteAndWirePayment();
    }

    setLoading(false);
  };

  const handleKonnectPayment = async () => {
    const userTimeZone = moment.tz.guess();
    const data = {
      sessionId: session.id,
      amount: price || 0,
      currency: userStore.currency || "TND",
      userTimeZone,
    };
    sendInitiateCheckoutEvent("konnect");
    const result = await payForSession(data);

    if (result) {
      if (result.payUrl) {
        window.location.href = result.payUrl;
      } else {
        if (result.success === true) {
          navigate("/payment-success/" + session.id);
        } else {
          toast.error("Something went wrong !");
        }
      }
    } else {
      toast.error("Something went wrong !");
    }
  };

  const handleOnSiteAndWirePayment = async () => {
    try {
      const response = await paymentStore.createWiringOrOnsitePaymentSession(
        session.id,
        selectedMethod
      );
      if (response?.status === 200) {
        navigate("/enrolled/" + session.id);
      } else {
        navigate("/paymentFailed");
      }
    } catch (error) {
      console.error("Error during Onsite/Wiring Payment:", error);
      navigate("/paymentFailed");
    }
  };

  const sendInitiateCheckoutEvent = async (paymentMethod: string = "") => {
    if (id) return;

    const payload = {
      event_name: "initiate_checkout",
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      action_source: "website",
      user_data: {
        client_user_agent: navigator.userAgent,
        em: [userStore.user?.email || ""],
        ph: [userStore.user?.phone || ""],
        country: userStore.user?.country || "",
        ge: userStore.user?.gender || "",
        fn: userStore.user?.fullName || "",
        ln: userStore.user?.fullName || "",
      },
      custom_data: {
        class_id: id,
        class_name: session?.title || "Unknown Class",
        payment_method: paymentMethod,
      },
    };

    try {
      console.log("payload", payload);
      //await userStore.sendMetaEvent("course_view", payload);
    } catch (error) {
      console.error("Error sending event:", error);
    }
  };

  return (
    <div
      className="flex flex-col gap-6 px-6 py-5 sm:p-10 w-full sm:w-3/5 h-fit bg-white rounded-[30px]"
      style={{ boxShadow: "0px 18px 26px -4px #00000005" }}
    >
      <form
        className="flex flex-col gap-3 sm:gap-5 w-full h-full"
        onSubmit={pay}
      >
        <h3 className="body-medium-bold">Enroll into Class</h3>
        {loading ? (
          <div className="flex items-center justify-center w-full h-full">
            <svg
              aria-hidden="true"
              className="w-12 h-12 text-gray-200 animate-spin fill-[#E83C70]"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3.5">
              <div className="flex flex-col gap-1.5 sm:gap-2.5 pb-5 border-b border-[#DBDBDB]">
                <h4 className="body-small-bold text-[#676767]">Your Details</h4>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  label="Full Name"
                  placeholder="Enter your Name..."
                  validation={validateName}
                  required
                />
                <Input
                  value={email}
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  label="Email Address"
                  placeholder="Enter your Email..."
                  validation={validateEmail}
                  required
                />
                <Input
                  value={phone}
                  type="number"
                  onChange={(e) => setPhone(e.target.value)}
                  label="Phone Number"
                  placeholder="+216"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5 sm:gap-2.5">
                <h4 className="body-small-bold text-[#676767]">
                  Payment Details
                </h4>
                <div className="flex flex-col lg:flex-row gap-1.5 w-full">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.name}
                      className={`w-full px-5 py-3.5 border rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedMethod === method.name
                          ? "bg-primary-paradise-pink-100 border-primary-paradise-pink-500 text-primary-paradise-pink-500"
                          : "bg-[#FAFAFA] border-[#E8E8E8] text-primary-gray-800 hover:border-[#BDBDBD]"
                      }`}
                      onClick={() => setSelectedMethod(method.name)}
                    >
                      {method.icon}
                      <span className="body-small">{method.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <Button
                type="primary_dark"
                text={
                  selectedMethod === "Konnect"
                    ? `Pay ${price} TND`
                    : "Enroll now"
                }
                isLoading={loading}
                submit
                icon={
                  selectedMethod === "Konnect"
                    ? "/images/icons/konnect.svg"
                    : undefined
                }
              />
              <p className="caption-text text-primary-gray-400">
                By pressing Enroll, you agree to our{" "}
                <a
                  className="underline hover:text-[#363636] transition-all duration-200"
                  href="https://blog.classx.tech/terms"
                  target="_blank"
                >
                  Terms of Service
                </a>{" "}
                &{" "}
                <a
                  className="underline hover:text-[#363636] transition-all duration-200"
                  href="https://blog.classx.tech/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default TunisianCheckoutForm;
